'use client'

import { IRedux } from '@/app/[locale]/_store/types'
import type { ICategory, IProduct } from '@/app/[locale]/_types'
import { Box, Button, Container } from '@chakra-ui/react'
import { memo, Suspense, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import CategoriesSection from './components/categories-section'
import FilteredProductsView from './components/filtered-products-view'
import LoadingSpinner from './components/loading-spinner'
import { useFilteredProductsQuery } from './hooks/use-filtered-products-query'
import { useCategoriesQuery } from './hooks/use-products-query'
import { SkeletonCategories, SkeletonProducts } from './loading'
import { useFilterParams, useMenuContext } from './utils/client'

interface FilteredDataType {
  products: IProduct[]
  count: number
}

import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { LuArrowDown, LuArrowUpRight } from 'react-icons/lu'
import type { InitialProductsData } from './InitialProductsServer'
import Section from './Section'
import { useRouter } from 'next/navigation'
import { IDiscount } from '@/app/[locale]/_types'
import { isTenantLocaleSettled } from '@/app/[locale]/_utils/shipper-switch'
import { resolveSourceId } from '@/app/[locale]/_utils/aggregator-context'
import { getCategoryWithProductsList } from '@/app/[locale]/_service/category.service'
import { useDynamicLanguage } from '@/app/[locale]/_hooks/useShipper'

interface ProductsProps {
  data?: InitialProductsData | null
  category?: ICategory | null
  discounts?: IDiscount[]
}

/** Backend truncates home category-with-products embeds at this length; match Multi. */
const HOME_PRODUCTS_PREVIEW_LIMIT = 30

/**
 * Products component with server-side initial data:
 *
 * Performance optimizations:
 * 1. Categories and first 2 sections' products are fetched on server
 * 2. Additional categories/products load on client as needed
 * 3. "View more" button navigates to category page instead of infinite scroll
 */
const Products = memo(function Products({
  data,
  category,
  discounts,
}: ProductsProps) {
  const t = useTranslations()
  const router = useRouter()
  const { tagIds, sort, isFiltered } = useFilterParams()
  const settings = useSelector((state: IRedux) => state.settings)
  const source_id = useSelector((state: IRedux) =>
    resolveSourceId(state.session, state.auth)
  )
  const catalogRef = useRef<HTMLDivElement | null>(null)
  const productsContainerRef = useRef<HTMLDivElement | null>(null)
  const filteredLoadMoreRef = useRef<HTMLDivElement | null>(null)
  const hasRefreshedForMenu = useRef(false)

  const serverMenuId = data?.params?.menu_id as string | undefined
  const { platform_id, effectiveMenuId, deliveryType, is_hall } =
    useMenuContext(serverMenuId)

  const shipperId = settings?.data?.shipper_id || ''
  const categoryNavigation = settings.data?.data?.category_navigation
  const isSeperatePage =
    (categoryNavigation
      ? categoryNavigation === 'page'
      : settings.data?.data.menu_view === 'marketplace') || Boolean(category)

  const { useDynamicLanguage: dynamic, isShipperResolved } =
    useDynamicLanguage()

  const {
    data: clientData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
  } = useCategoriesQuery(
    shipperId,
    platform_id,
    effectiveMenuId,
    deliveryType,
    is_hall === 'true',
    false,
    !!data?.categories?.length,
    source_id,
    dynamic,
    isShipperResolved
  )

  // Mirror SSR category-with-products on the client so the request is visible in DevTools Network
  useEffect(() => {
    if (!shipperId || !platform_id || !deliveryType || !isShipperResolved) return

    void getCategoryWithProductsList(
      {
        page: 1,
        limit: 30,
        is_active: true,
        has_product: true,
        menu_id: effectiveMenuId,
        delivery_type: deliveryType,
        fields: 'discounts',
        ...(source_id && { aggregator_id: source_id }),
      },
      shipperId,
      platform_id,
      dynamic
    )
  }, [shipperId, platform_id, effectiveMenuId, deliveryType, source_id, dynamic, isShipperResolved])

  // Merge server initial data with client data
  // IMPORTANT: Always preserve products from server data (getCategoryWithProductsList)
  // Client data (getCategoryList) doesn't include products, so we merge them properly
  const categories = useMemo(() => {
    if (data?.categories?.length) {
      const categories = data.categories.concat(clientData?.categories || [])
      const flat_categories = data.flat_categories.concat(
        clientData?.flat_categories || []
      )
      const count = data.count

      return {
        categories,
        flat_categories,
        count,
      }
    }

    // Fallback to client data if no server data
    return clientData
  }, [data, clientData])

  // Filtered products query (only active when filtering)
  const {
    data: filteredData,
    fetchNextPage: fetchNextFilteredPage,
    hasNextPage: hasNextFilteredPage,
    isFetchingNextPage: isFetchingNextFilteredPage,
    isLoading: isFilteredLoading,
  } = useFilteredProductsQuery(isFiltered, shipperId, platform_id, {
    menu_id: effectiveMenuId,
    delivery_type: deliveryType,
    tag_ids: tagIds,
    sort,
    category_id: category?.id,
    source_id,
  })

  // Compute categories to display based on view mode and selection
  const categoriesToRender = useMemo(() => {
    // If a specific category is selected, show only that category
    if (category?.slug) {
      // const categoryData = clientData?.categories.find(
      //   (item) => item.slug === category.slug
      // )
      // if (categoryData) {
      //   return [categoryData]
      // }
      return [category]
    }

    // Use flat categories for standard view, regular categories for marketplace
    return (
      (isSeperatePage ? categories?.categories : categories?.flat_categories) ||
      []
    )
  }, [
    categories?.categories,
    categories?.flat_categories,
    category,
    isSeperatePage,
  ])

  const filteredDataToRender: FilteredDataType = useMemo(
    () => filteredData ?? { products: [], count: 0 },
    [filteredData]
  )

  useEffect(() => {
    if (hasRefreshedForMenu.current) return

    if (!data?.params || serverMenuId) return

    if (!effectiveMenuId) return

    if (!shipperId) return

    if (!isTenantLocaleSettled(shipperId)) return

    hasRefreshedForMenu.current = true

    const timeoutId = setTimeout(() => {
      router.refresh()
    }, 500)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [data?.params, serverMenuId, effectiveMenuId, shipperId, router])

  // Loading state - show skeleton only if no initial data
  if (isLoading && !categories) {
    return (
      <Container px={{ base: 4, lg: 10 }}>
        <SkeletonCategories />
        <SkeletonProducts />
      </Container>
    )
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      {/* Categories navigation */}
      <CategoriesSection
        count={categories?.count}
        active={category?.slug}
        catalogRef={catalogRef}
        serverData={categoriesToRender}
        isSeperatePage={isSeperatePage}
      />

      {/* Products section */}
      <Container px={{ base: 4, lg: 10 }} h='full' ref={productsContainerRef}>
        {isFiltered && isFilteredLoading ? (
          <SkeletonProducts />
        ) : isFiltered ? (
          <FilteredProductsView
            filteredData={filteredDataToRender}
            isFetchingNextPage={isFetchingNextFilteredPage}
            hasNextPage={hasNextFilteredPage}
            loadMoreRef={filteredLoadMoreRef}
          />
        ) : categoriesToRender.length > 0 ? (
          categoriesToRender?.map((item, idx) => (
            <div key={`${item?.id}-${item?.slug}-${idx}-${item?.image}`}>
              <Section
                initial={idx < 1}
                category={item}
                products={item?.products}
                discounts={discounts}
              />
              {/* View more button - navigates to category page */}
              {!category?.slug &&
                item?.slug &&
                item?.products?.length === HOME_PRODUCTS_PREVIEW_LIMIT && (
                  <Box textAlign='center' pt={4}>
                    <Button
                      h={14}
                      px={4}
                      gap={2}
                      size='lg'
                      rounded={16}
                      variant='outline'
                      borderColor='#00000014'
                      asChild
                    >
                      <Link href={`/category/${item.slug}`}>
                        {t('show_more')}
                        <LuArrowUpRight />
                      </Link>
                    </Button>
                  </Box>
                )}
            </div>
          ))
        ) : (
          <SkeletonProducts />
        )}
        {(hasNextPage ||
          (categories?.categories?.length || 0) <
            Number(categories?.count)) && (
          <Box textAlign='center' pt={4}>
            <Button
              h={14}
              px={4}
              gap={2}
              size='lg'
              rounded={16}
              variant='outline'
              borderColor='#00000014'
              onClick={() => fetchNextPage()}
              loading={isFetchingNextPage}
              disabled={isFetchingNextPage}
            >
              <LuArrowDown />
              {t('more_load')}
            </Button>
          </Box>
        )}
      </Container>
    </Suspense>
  )
})

export default Products
